/**
 * Email Triggers API Service
 *
 * Handles interactions with email trigger management endpoints
 * Mirrors the pattern from Email API for consistency
 */
import { apiRequest } from './client';

/**
 * Email Trigger Rule interface
 */
export interface EmailTriggerRule {
  id: string;
  organisation_id: string;
  restaurant_id?: string | null;
  rule_name: string;
  rule_description?: string;
  trigger_type: string;
  rule_conditions: Record<string, any>;
  time_delay: { value: number; unit: string };
  send_time_preference?: Record<string, any>;
  email_subject: string;
  email_template_id?: string | null;
  email_content_html: string;
  email_content_text?: string;
  email_design_json?: Record<string, any> | null; // GrapesJS project data
  personalization_fields?: string[];
  is_active?: boolean;
  priority?: number;
  max_sends_per_customer?: number;
  cooldown_days?: number;
  starts_at?: string;
  expires_at?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
  email_domain_id?: string;
  sender_email?: string;
  sender_name?: string;
}

/**
 * Trigger list response interface
 */
interface TriggerListResponse {
  success: boolean;
  data: EmailTriggerRule[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
}

/**
 * Generic API response interface
 */
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/**
 * Email Trigger Service for interacting with email trigger APIs
 */
const EmailTriggerService = {
  /**
   * ==================== TRIGGER CRUD ENDPOINTS ====================
   */

  /**
   * Fetch email triggers for an organisation
   */
  fetchTriggers: async (
    organisationId: string,
    filters?: {
      is_active?: boolean;
      trigger_type?: string;
      sort_by?: string;
      sort_order?: 'asc' | 'desc';
      limit?: number;
      offset?: number;
    }
  ): Promise<EmailTriggerRule[]> => {
    try {
      console.log('[Email Triggers] Fetching triggers for organisation:', organisationId);

      const params = new URLSearchParams({
        organisation_id: organisationId,
      });

      if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());
      if (filters?.trigger_type) params.append('trigger_type', filters.trigger_type);
      if (filters?.sort_by) params.append('sort_by', filters.sort_by);
      if (filters?.sort_order) params.append('sort_order', filters.sort_order);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const response = await apiRequest<TriggerListResponse>({
        method: 'GET',
        url: `/api/email/triggers?${params.toString()}`
      });

      if (response && response.success && response.data) {
        console.log(`[Email Triggers] Successfully fetched ${response.data.length} triggers`);
        return response.data;
      }

      throw new Error('Failed to fetch triggers');
    } catch (error) {
      console.error('[Email Triggers] Error fetching triggers:', error);
      throw error;
    }
  },

  /**
   * Get a single trigger by ID
   */
  getTrigger: async (triggerId: string, organisationId: string): Promise<EmailTriggerRule> => {
    try {
      console.log('[Email Triggers] Fetching trigger:', triggerId);

      const response = await apiRequest<{ success: boolean; data: EmailTriggerRule }>({
        method: 'GET',
        url: `/api/email/triggers/${triggerId}?organisation_id=${organisationId}`
      });

      if (response && response.success && response.data) {
        console.log('[Email Triggers] Successfully fetched trigger');
        return response.data;
      }

      throw new Error('Failed to fetch trigger');
    } catch (error) {
      console.error('[Email Triggers] Error fetching trigger:', error);
      throw error;
    }
  },

  /**
   * Create a new email trigger
   */
  createTrigger: async (triggerData: {
    organisation_id: string;
    restaurant_id?: string | null;
    rule_name: string;
    rule_description?: string;
    trigger_type: string;
    rule_conditions?: Record<string, any>;
    time_delay?: { value: number; unit: string };
    send_time_preference?: Record<string, any>;
    email_subject: string;
    email_template_id?: string | null;
    email_content_html: string;
    email_content_text?: string;
    email_design_json?: Record<string, any> | null;
    personalization_fields?: string[];
    is_active?: boolean;
    priority?: number;
    max_sends_per_customer?: number;
    cooldown_days?: number;
    starts_at?: string;
    expires_at?: string;
    email_domain_id?: string;
    sender_email?: string;
    sender_name?: string;
    created_by?: string;
  }): Promise<EmailTriggerRule> => {
    try {
      console.log('[Email Triggers] Creating trigger:', triggerData.rule_name);

      // Validate required fields
      if (!triggerData.organisation_id) {
        throw new Error('Organisation ID is required');
      }
      if (!triggerData.rule_name) {
        throw new Error('Trigger name is required');
      }
      if (!triggerData.email_subject) {
        throw new Error('Email subject is required');
      }
      if (!triggerData.email_content_html) {
        throw new Error('Email content is required');
      }

      const response = await apiRequest<{ success: boolean; data: EmailTriggerRule; error?: string }>({
        method: 'POST',
        url: '/api/email/triggers',
        data: triggerData
      });

      if (response && response.success && response.data) {
        console.log('[Email Triggers] Trigger created successfully:', response.data.id);
        return response.data;
      }

      throw new Error(response?.error || 'Failed to create trigger');
    } catch (error) {
      console.error('[Email Triggers] Error creating trigger:', error);
      throw error;
    }
  },

  /**
   * Update an existing trigger
   */
  updateTrigger: async (
    triggerId: string,
    updates: Partial<EmailTriggerRule>
  ): Promise<EmailTriggerRule> => {
    try {
      console.log('[Email Triggers] Updating trigger:', triggerId);

      const response = await apiRequest<{ success: boolean; data: EmailTriggerRule; error?: string }>({
        method: 'PUT',
        url: `/api/email/triggers/${triggerId}`,
        data: updates
      });

      if (response && response.success && response.data) {
        console.log('[Email Triggers] Trigger updated successfully');
        return response.data;
      }

      throw new Error(response?.error || 'Failed to update trigger');
    } catch (error) {
      console.error('[Email Triggers] Error updating trigger:', error);
      throw error;
    }
  },

  /**
   * Delete a trigger
   */
  deleteTrigger: async (triggerId: string, organisationId: string): Promise<void> => {
    try {
      console.log('[Email Triggers] Deleting trigger:', triggerId);

      const response = await apiRequest<ApiResponse>({
        method: 'DELETE',
        url: `/api/email/triggers/${triggerId}?organisation_id=${organisationId}`
      });

      if (response && response.success) {
        console.log('[Email Triggers] Trigger deleted successfully');
        return;
      }

      throw new Error(response?.error || 'Failed to delete trigger');
    } catch (error) {
      console.error('[Email Triggers] Error deleting trigger:', error);
      throw error;
    }
  },

  /**
   * Toggle trigger active state
   */
  toggleTrigger: async (
    triggerId: string,
    enabled: boolean,
    organisationId: string
  ): Promise<EmailTriggerRule> => {
    try {
      console.log('[Email Triggers] Toggling trigger:', triggerId, 'to', enabled);

      const response = await apiRequest<{ success: boolean; data: EmailTriggerRule; error?: string }>({
        method: 'POST',
        url: `/api/email/triggers/${triggerId}/toggle`,
        data: { enabled, organisation_id: organisationId }
      });

      if (response && response.success && response.data) {
        console.log('[Email Triggers] Trigger toggled successfully');
        return response.data;
      }

      throw new Error(response?.error || 'Failed to toggle trigger');
    } catch (error) {
      console.error('[Email Triggers] Error toggling trigger:', error);
      throw error;
    }
  },

  /**
   * Get trigger execution history
   */
  getTriggerHistory: async (
    triggerId: string,
    organisationId: string,
    options?: {
      page?: number;
      per_page?: number;
      status?: string;
      start_date?: string;
      end_date?: string;
    }
  ): Promise<{
    executions: any[];
    total: number;
    page: number;
    per_page: number;
  }> => {
    try {
      console.log('[Email Triggers] Fetching history for trigger:', triggerId);

      const params = new URLSearchParams({
        organisation_id: organisationId,
      });

      if (options?.page) params.append('page', options.page.toString());
      if (options?.per_page) params.append('per_page', options.per_page.toString());
      if (options?.status) params.append('status', options.status);
      if (options?.start_date) params.append('start_date', options.start_date);
      if (options?.end_date) params.append('end_date', options.end_date);

      const response = await apiRequest<ApiResponse<{
        executions: any[];
        total: number;
        page: number;
        per_page: number;
      }>>({
        method: 'GET',
        url: `/api/email/triggers/${triggerId}/history?${params.toString()}`
      });

      if (response && response.success && response.data) {
        console.log('[Email Triggers] History fetched successfully');
        return response.data;
      }

      throw new Error(response?.error || 'Failed to fetch trigger history');
    } catch (error) {
      console.error('[Email Triggers] Error fetching history:', error);
      throw error;
    }
  },

  /**
   * Preview trigger audience
   */
  previewTriggerAudience: async (
    organisationId: string,
    conditions: any[],
    logic: 'AND' | 'OR'
  ): Promise<{
    total_recipients: number;
    sample_recipients: any[];
  }> => {
    try {
      console.log('[Email Triggers] Previewing audience');

      const response = await apiRequest<ApiResponse<{
        total_recipients: number;
        sample_recipients: any[];
      }>>({
        method: 'POST',
        url: '/api/email/triggers/preview',
        data: {
          organisation_id: organisationId,
          conditions,
          logic
        }
      });

      if (response && response.success && response.data) {
        console.log('[Email Triggers] Audience preview:', response.data.total_recipients, 'recipients');
        return response.data;
      }

      throw new Error(response?.error || 'Failed to preview audience');
    } catch (error) {
      console.error('[Email Triggers] Error previewing audience:', error);
      throw error;
    }
  },

  /**
   * Preview trigger with resolved variables for a specific customer
   * Similar to campaign preview but for triggers
   *
   * @param triggerId - Trigger UUID
   * @param customerId - Customer UUID
   * @param organisationId - Organisation UUID
   * @returns Resolved preview with actual customer data
   */
  previewTriggerWithCustomer: async (
    triggerId: string,
    customerId: string,
    organisationId: string
  ): Promise<{
    html: string;
    subject: string;
    resolved_variables: Record<string, Record<string, string>>;
    customer: {
      id: string;
      email: string;
      first_name: string;
      last_name: string;
    };
  }> => {
    try {
      console.log('[Email Triggers] Generating preview with customer data:', customerId);

      const response = await apiRequest<{
        success: boolean;
        data: {
          html: string;
          subject: string;
          resolved_variables: Record<string, Record<string, string>>;
          customer: {
            id: string;
            email: string;
            first_name: string;
            last_name: string;
          };
        };
        error?: string;
      }>({
        method: 'POST',
        url: '/api/email/triggers/preview-with-customer',
        data: {
          trigger_id: triggerId,
          customer_id: customerId,
          organisation_id: organisationId
        }
      });

      if (response && response.success && response.data) {
        console.log('[Email Triggers] Preview generated successfully');
        return response.data;
      }

      throw new Error(response?.error || 'Failed to generate preview');
    } catch (error) {
      console.error('[Email Triggers] Error generating preview:', error);
      throw error;
    }
  },

  /**
   * ==================== TEMPLATE ENDPOINTS ====================
   */

  /**
   * Get all trigger templates
   * Templates provide pre-configured trigger structures for common use cases
   */
  getTriggerTemplates: async (): Promise<any[]> => {
    try {
      console.log('[Email Triggers] Fetching trigger templates');

      const response = await apiRequest<{
        success: boolean;
        data: any[];
        error?: string;
      }>({
        method: 'GET',
        url: '/api/email/triggers/templates'
      });

      if (response && response.success && response.data) {
        console.log('[Email Triggers] Successfully fetched', response.data.length, 'templates');
        return response.data;
      }

      throw new Error(response?.error || 'Failed to fetch templates');
    } catch (error) {
      console.error('[Email Triggers] Error fetching templates:', error);
      throw error;
    }
  }
};

export default EmailTriggerService;